import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';

import { EmptyStateMessage, Section, SectionCard, SectionHeader } from '@/components/common';
import { alwaysSeesEmptySections } from '@/auth/sectionVisibility';
import {
  useInterviewSummary,
  useAddInterviewSummaryEntry,
  useDeleteInterviewSummaryEntry,
  useUpdateInterviewCandidate,
  useStaffSearch,
  useInterviewSummaryReport,
} from '@/queries/useAppraisalDetail';
import type { InterviewStaffSearchResult } from '@/api/types/appraisal.types';
import type { RecordAccess } from '@/auth/permissions.types';

type SortField = 'staffName' | 'staffRole' | 'totalDurationWeeks' | 'source';
type SortDir = 'asc' | 'desc';

interface InterviewSummarySectionProps {
  appraisalId: number;
  permissions: RecordAccess | undefined;
  validationTriggered?: boolean;
}

export function InterviewSummarySection({
  appraisalId,
  permissions,
  validationTriggered = false,
}: InterviewSummarySectionProps) {
  const sectionAccess = permissions?.sections.interviewSummary;
  const canEditSuggested = sectionAccess?.canEditSuggested ?? false;
  const canEditPlanned = sectionAccess?.canEditPlanned ?? false;
  const canEditCompleted = sectionAccess?.canEditCompleted ?? false;
  const canAdd = sectionAccess?.canAdd ?? false;
  const canDelete = sectionAccess?.canDelete ?? false;
  const canDownloadReport = sectionAccess?.canDownloadReport ?? false;

  const [sortField, setSortField] = useState<SortField>('staffName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const [selectedStaff, setSelectedStaff] = useState<InterviewStaffSearchResult | null>(null);
  const [reason, setReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: entries = [], isLoading } = useInterviewSummary(appraisalId);
  const { data: searchResults = [], isFetching: isSearching } = useStaffSearch(appraisalId, searchQuery);
  const { mutate: addEntry, isPending: isAdding } = useAddInterviewSummaryEntry();
  const { mutate: deleteEntry } = useDeleteInterviewSummaryEntry();
  const { mutate: updateCandidate } = useUpdateInterviewCandidate();
  const { mutate: downloadReport, isPending: isDownloading } = useInterviewSummaryReport(appraisalId);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedEntries = [...entries].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    const cmp =
      typeof aVal === 'string' && typeof bVal === 'string'
        ? aVal.localeCompare(bVal)
        : (aVal as number) - (bVal as number);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleAdd = () => {
    if (!selectedStaff) return;
    addEntry(
      { appraisalId, data: { staffId: selectedStaff.staffId, reason: reason || null } },
      {
        onSuccess: () => {
          setSelectedStaff(null);
          setReason('');
          setSearchQuery('');
        },
      }
    );
  };

  const handleDelete = (candidateId: number) => {
    deleteEntry({ appraisalId, candidateId });
  };

  const handleCheckboxChange = (
    candidateId: number,
    field: 'appraiseSuggested' | 'planned' | 'completed',
    value: boolean
  ) => {
    updateCandidate({ appraisalId, candidateId, data: { [field]: value } });
  };

  const noPlanned = !entries.some((e) => e.planned);
  const noCompleted = !entries.some((e) => e.completed);
  const plannedError = validationTriggered && noPlanned;
  const completedError = validationTriggered && noCompleted;
  const hasError = plannedError || completedError;

  if (isLoading) {
    return (
      <Section>
        <SectionHeader>INTERVIEW CANDIDATES</SectionHeader>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Section>
    );
  }

  const isReadOnly = !canAdd && !canEditSuggested && !canEditPlanned && !canEditCompleted;
  if (isReadOnly && sortedEntries.length === 0 && !alwaysSeesEmptySections(permissions?.role)) {
    return null;
  }

  return (
    <Section>
      <SectionHeader
        error={hasError}
        action={
          canDownloadReport ? (
            <Button
              size="small"
              variant="text"
              startIcon={<DownloadIcon />}
              onClick={() => downloadReport()}
              disabled={isDownloading}
              sx={{ color: 'common.white' }}
            >
              Download Report
            </Button>
          ) : undefined
        }
      >
        INTERVIEW CANDIDATES
      </SectionHeader>
      <SectionCard error={hasError}>
        {canAdd && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
            <Typography variant="body1" sx={{ fontWeight: 500, flexShrink: 0 }}>
              Add another person:
            </Typography>
            <Autocomplete
              sx={{ flex: 1, maxWidth: 320 }}
              options={searchResults}
              getOptionLabel={(opt) => `${opt.fullName} (${opt.role})`}
              loading={isSearching}
              value={selectedStaff}
              onChange={(_, val) => setSelectedStaff(val)}
              onInputChange={(_, val) => setSearchQuery(val)}
              isOptionEqualToValue={(opt, val) => opt.staffId === val.staffId}
              filterOptions={(x) => x}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Name and surname"
                  placeholder="Start typing to search..."
                  variant="standard"
                  InputLabelProps={{ shrink: true, sx: { fontSize: '1rem', transform: 'translate(0, -3px)' } }}
                  sx={{ '& .MuiInput-root': { mt: 3 }, '& .MuiInput-input::placeholder': { fontStyle: 'italic' } }}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isSearching ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
            <TextField
              label="Reason for adding"
              placeholder="Type the reason..."
              variant="standard"
              size="small"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{ flex: 1, maxWidth: 320, '& .MuiInput-root': { mt: 3 }, '& .MuiInput-input::placeholder': { fontStyle: 'italic' } }}
              InputLabelProps={{ shrink: true, sx: { fontSize: '1rem', transform: 'translate(0, -3px)' } }}
            />
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={!selectedStaff || !reason.trim() || isAdding}
              sx={{ ml: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Add
            </Button>
          </Box>
        )}

        {sortedEntries.length === 0 ? (
          <EmptyStateMessage message="No interview candidates available." />
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  bgcolor: '#FFE082',
                  border: '1px solid #C2C7C8',
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Was a Job End author
              </Typography>
            </Box>

            <Table size="small">
              <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'staffName'}
                  direction={sortField === 'staffName' ? sortDir : 'asc'}
                  onClick={() => handleSort('staffName')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              {(sectionAccess?.showRoles ?? true) && (
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'staffRole'}
                    direction={sortField === 'staffRole' ? sortDir : 'asc'}
                    onClick={() => handleSort('staffRole')}
                  >
                    Role
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell>
                <TableSortLabel
                  active={sortField === 'totalDurationWeeks'}
                  direction={sortField === 'totalDurationWeeks' ? sortDir : 'asc'}
                  onClick={() => handleSort('totalDurationWeeks')}
                >
                  Total Duration
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'source'}
                  direction={sortField === 'source' ? sortDir : 'asc'}
                  onClick={() => handleSort('source')}
                >
                  Source
                </TableSortLabel>
              </TableCell>
              <TableCell>Reason</TableCell>
              <TableCell align="center">Suggest</TableCell>
              <TableCell
                align="center"
                sx={plannedError ? { color: 'error.main', fontWeight: 600 } : undefined}
                data-testid={plannedError ? 'field-error' : undefined}
              >
                Planned
              </TableCell>
              <TableCell
                align="center"
                sx={completedError ? { color: 'error.main', fontWeight: 600 } : undefined}
                data-testid={completedError ? 'field-error' : undefined}
              >
                Completed
              </TableCell>
              {canDelete && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedEntries.map((entry) => {
              const isProjectSource = entry.source === 'Project';
              const isMentorSource = entry.source === 'Mentor';
              const canDeleteRow = canDelete && !isProjectSource && !isMentorSource;
              const primaryCandidateId = entry.candidateIds[0];

              return (
                <TableRow key={entry.staffId}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {entry.isJobEndAuthor && (
                        <Box
                          aria-label="Job end author"
                          sx={{
                            width: 10,
                            height: 10,
                            bgcolor: '#FFE082',
                            border: '1px solid #C2C7C8',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {entry.staffName}
                    </Box>
                  </TableCell>
                  {(sectionAccess?.showRoles ?? true) && (
                    <TableCell>{entry.staffRole || '-'}</TableCell>
                  )}
                  <TableCell>{entry.totalDurationWeeks} weeks</TableCell>
                  <TableCell>{entry.source}</TableCell>
                  <TableCell>
                    {entry.reasons.length > 0 ? entry.reasons.join('; ') : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={entry.appraiseSuggested}
                      disabled={!canEditSuggested}
                      onChange={(e) =>
                        handleCheckboxChange(primaryCandidateId, 'appraiseSuggested', e.target.checked)
                      }
                      inputProps={{ 'aria-label': `Suggest ${entry.staffName}` }}
                      sx={!canEditSuggested ? { color: 'action.disabled' } : undefined}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={entry.planned}
                      disabled={!canEditPlanned}
                      onChange={(e) =>
                        handleCheckboxChange(primaryCandidateId, 'planned', e.target.checked)
                      }
                      inputProps={{ 'aria-label': `Plan ${entry.staffName}` }}
                      sx={!canEditPlanned ? { color: 'action.disabled' } : undefined}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={entry.completed}
                      disabled={!canEditCompleted}
                      onChange={(e) =>
                        handleCheckboxChange(primaryCandidateId, 'completed', e.target.checked)
                      }
                      inputProps={{ 'aria-label': `Complete ${entry.staffName}` }}
                      sx={!canEditCompleted ? { color: 'action.disabled' } : undefined}
                    />
                  </TableCell>
                  {canDelete && (
                    <TableCell>
                      {canDeleteRow && (
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(primaryCandidateId)}
                          aria-label={`Delete ${entry.staffName}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
          </>
        )}
      </SectionCard>
    </Section>
  );
}

export default InterviewSummarySection;
